import { faLayerGroup } from '@fortawesome/pro-light-svg-icons/faLayerGroup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PageResult } from '@spike/model';
import { Room } from '@spike/room-model';
import { fetchThunk as fetchRoomsThunk } from '@spike/rooms-action';
import Button from 'components/UI/V3/Button/Button';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import { useApiClientWrapper } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import styles from './RoomsDrawerFilter.module.scss';

interface RoomsDrawerFilterProps {
    // Selected room ids. An empty array means "All Rooms" (no filter).
    selected: Array<number>;
    className?: string;
    onChangeSelection?: (selectedRoomIds: Array<number>) => void;
}

export const RoomsDrawerFilter: FunctionComponent<RoomsDrawerFilterProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const { organizationMarketplaces } = useOrganizationMarketplaces();
    const [open, setOpen] = useState(false);

    const marketplaceIds = useMemo(
        () => organizationMarketplaces.map(marketplace => marketplace.id),
        [organizationMarketplaces]
    );

    const rooms = useSelector<RootState, PageResult<Room>>(state => state.rooms.rooms);

    useEffect(() => {
        dispatch(fetchRoomsThunk(apiClientWrapper, 1, 3210, marketplaceIds));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const options = useMemo(
        () =>
            rooms.page
                .filter(room => room.id != null)
                .map(room => ({ id: room.id!, name: room.name }))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [rooms.page]
    );

    const allIds = options.map(option => option.id);

    const [explicitNone, setExplicitNone] = useState(false);

    useEffect(() => {
        if (props.selected.length > 0) {
            setExplicitNone(false);
        }
    }, [props.selected]);

    const isAll = !explicitNone && props.selected.length === 0;
    const isChecked = (id: number) => !explicitNone && (props.selected.length === 0 || props.selected.includes(id));

    const toggleAll = (checked: boolean) => {
        setExplicitNone(!checked);
        props.onChangeSelection?.([]);
    };

    const toggleRoom = (id: number, checked: boolean) => {
        const base = explicitNone ? [] : props.selected.length === 0 ? allIds : props.selected;
        const next = new Set(base);
        checked ? next.add(id) : next.delete(id);

        if (next.size === 0) {
            setExplicitNone(true);
            props.onChangeSelection?.([]);
            return;
        }

        setExplicitNone(false);
        props.onChangeSelection?.(next.size === allIds.length ? [] : Array.from(next));
    };

    const label = isAll ? 'All Rooms' : `${explicitNone ? 0 : props.selected.length} rooms`;

    return (
        <div className={props.className}>
            <Dropdown
                open={open}
                onOpenChange={setOpen}
            >
                <DropdownTrigger>
                    <Button
                        className={styles.button}
                        variant="outlined"
                        size="large"
                        leftIcon={faLayerGroup}
                        label={label}
                    />
                    <div className={styles.mobileButton}>
                        <FontAwesomeIcon icon={faLayerGroup} />
                    </div>
                </DropdownTrigger>
                <DropdownContent
                    align="start"
                    minWidth={220}
                    maxHeight={320}
                >
                    <DropdownItem
                        type="checkbox"
                        checked={isAll}
                        onSelect={toggleAll}
                    >
                        All Rooms
                    </DropdownItem>
                    {options.map(option => (
                        <DropdownItem
                            key={option.id}
                            type="checkbox"
                            checked={isChecked(option.id)}
                            onSelect={checked => toggleRoom(option.id, checked)}
                        >
                            {option.name}
                        </DropdownItem>
                    ))}
                </DropdownContent>
            </Dropdown>
        </div>
    );
};

export default RoomsDrawerFilter;
